import {
  ChallengeApiError,
  parseChallengeBootstrapToken,
  parseChallengeOperationId,
  parseChallengeDeckCopyResult,
  parseChallengesProjection,
  parseChallengeStartResult,
  parseChallengeTrackId,
  type ChallengeApi,
  type ChallengeApiErrorCode,
} from './api.js';

export type ChallengeBrowserFetch = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

export interface BrowserChallengeApiOptions {
  readonly fetch?: ChallengeBrowserFetch;
  readonly generateOperationId?: () => string;
  readonly generateBootstrapToken?: () => string;
}

const ERROR_CODES: ReadonlySet<ChallengeApiErrorCode> = new Set([
  'ACCOUNT_REQUIRED', 'PROFILE_INCOMPLETE', 'FORBIDDEN_ORIGIN',
  'CHALLENGE_NOT_FOUND', 'CHALLENGE_STAGE_LOCKED', 'OPERATION_ID_CONFLICT',
  'DECK_NOT_FOUND', 'DECK_LIMIT_REACHED', 'DECK_INVALID', 'DECK_NAME_INVALID',
  'INVALID_REQUEST', 'REQUEST_TOO_LARGE', 'RATE_LIMITED', 'SERVICE_UNAVAILABLE', 'INTERNAL_ERROR',
  'METHOD_NOT_ALLOWED', 'NOT_FOUND',
]);

const ERROR_STATUS: Readonly<Record<Exclude<ChallengeApiErrorCode, 'INVALID_RESPONSE'>, number>> = {
  ACCOUNT_REQUIRED: 401,
  PROFILE_INCOMPLETE: 403,
  FORBIDDEN_ORIGIN: 403,
  CHALLENGE_NOT_FOUND: 404,
  CHALLENGE_STAGE_LOCKED: 409,
  OPERATION_ID_CONFLICT: 409,
  DECK_NOT_FOUND: 404,
  DECK_LIMIT_REACHED: 409,
  DECK_INVALID: 400,
  DECK_NAME_INVALID: 400,
  INVALID_REQUEST: 400,
  REQUEST_TOO_LARGE: 413,
  RATE_LIMITED: 429,
  SERVICE_UNAVAILABLE: 503,
  INTERNAL_ERROR: 500,
  METHOD_NOT_ALLOWED: 405,
  NOT_FOUND: 404,
};

function mediaType(response: Response): string | undefined {
  return response.headers.get('content-type')?.split(';', 1)[0]?.trim().toLowerCase();
}

async function readJson(response: Response): Promise<unknown> {
  if (mediaType(response) !== 'application/json') {
    throw new ChallengeApiError('INVALID_RESPONSE', response.status);
  }
  try {
    return await response.json();
  } catch {
    throw new ChallengeApiError('INVALID_RESPONSE', response.status);
  }
}

async function throwResponseError(response: Response): Promise<never> {
  try {
    const payload = await readJson(response);
    if (typeof payload !== 'object' || payload === null || Array.isArray(payload)) throw new Error();
    const object = payload as Record<string, unknown>;
    const code = object.error;
    if (Object.keys(object).length !== 1 || typeof code !== 'string'
      || !ERROR_CODES.has(code as ChallengeApiErrorCode)) throw new Error();
    const typedCode = code as Exclude<ChallengeApiErrorCode, 'INVALID_RESPONSE'>;
    if (ERROR_STATUS[typedCode] !== response.status) throw new Error();
    throw new ChallengeApiError(typedCode, response.status);
  } catch (error) {
    if (error instanceof ChallengeApiError) throw error;
    throw new ChallengeApiError('INVALID_RESPONSE', response.status);
  }
}

async function expectParsed<T>(
  response: Response,
  statuses: readonly number[],
  parser: (value: unknown) => T,
): Promise<T> {
  if (!statuses.includes(response.status)) return throwResponseError(response);
  try {
    return parser(await readJson(response));
  } catch (error) {
    if (error instanceof ChallengeApiError) throw error;
    throw new ChallengeApiError('INVALID_RESPONSE', response.status);
  }
}

function readInit(signal?: AbortSignal): RequestInit {
  return {
    method: 'GET',
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
    mode: 'same-origin',
    redirect: 'error',
    cache: 'no-store',
    ...(signal === undefined ? {} : { signal }),
  };
}

function startInit(
  operationId: string,
  bootstrapToken: string,
  csrfToken: string,
  signal?: AbortSignal,
): RequestInit {
  if (csrfToken.length === 0) throw new Error('csrfToken must be non-empty');
  return {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken,
    },
    credentials: 'same-origin',
    mode: 'cors',
    redirect: 'error',
    cache: 'no-store',
    body: JSON.stringify({ operationId, bootstrapToken }),
    ...(signal === undefined ? {} : { signal }),
  };
}

function copyDeckInit(
  operationId: string,
  csrfToken: string,
  signal?: AbortSignal,
): RequestInit {
  if (csrfToken.length === 0) throw new Error('csrfToken must be non-empty');
  return {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken,
    },
    credentials: 'same-origin',
    mode: 'cors',
    redirect: 'error',
    cache: 'no-store',
    body: JSON.stringify({ operationId }),
    ...(signal === undefined ? {} : { signal }),
  };
}

const BASE64URL = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

function randomBootstrapToken(): string {
  const bytes = new Uint8Array(32);
  globalThis.crypto.getRandomValues(bytes);
  let result = '';
  let buffer = 0;
  let bits = 0;
  for (const byte of bytes) {
    buffer = (buffer << 8) | byte;
    bits += 8;
    while (bits >= 6) {
      bits -= 6;
      result += BASE64URL[(buffer >>> bits) & 63];
    }
  }
  if (bits > 0) result += BASE64URL[(buffer << (6 - bits)) & 63];
  return result;
}

/** Account-only same-origin adapter; the account bearer remains in its HttpOnly cookie. */
export function createBrowserChallengeApi(options: BrowserChallengeApiOptions = {}): ChallengeApi {
  const browserFetch = options.fetch ?? globalThis.fetch.bind(globalThis);
  const generateOperationId = options.generateOperationId ?? (() => globalThis.crypto.randomUUID());
  const generateBootstrapToken = options.generateBootstrapToken ?? randomBootstrapToken;
  const pendingStarts = new Map<string, Readonly<{
    operationId: string;
    bootstrapToken: string;
  }>>();
  const pendingCopies = new Map<string, string>();
  return {
    async list(signal) {
      const response = await browserFetch('/api/challenges', readInit(signal));
      return expectParsed(response, [200], parseChallengesProjection);
    },
    async start(trackId, stage, csrfToken, startOptions = {}) {
      const id = parseChallengeTrackId(trackId);
      if (stage !== 1 && stage !== 2 && stage !== 3) throw new Error('stage: expected 1, 2 or 3');
      const retryKey = `${id}:${stage}`;
      const remembered = startOptions.operationId === undefined
        && startOptions.bootstrapToken === undefined
        ? pendingStarts.get(retryKey)
        : undefined;
      const operationId = parseChallengeOperationId(
        startOptions.operationId ?? remembered?.operationId ?? generateOperationId(),
      );
      const bootstrapToken = parseChallengeBootstrapToken(
        startOptions.bootstrapToken ?? remembered?.bootstrapToken ?? generateBootstrapToken(),
      );
      if ((startOptions.operationId === undefined) !== (startOptions.bootstrapToken === undefined)) {
        throw new Error('operationId and bootstrapToken must be provided together');
      }
      const callerCredentials = startOptions.operationId !== undefined;
      if (!callerCredentials && remembered === undefined) {
        if (pendingStarts.size >= 32) {
          const oldest = pendingStarts.keys().next().value as string | undefined;
          if (oldest !== undefined) pendingStarts.delete(oldest);
        }
        pendingStarts.set(retryKey, { operationId, bootstrapToken });
      }
      try {
        const response = await browserFetch(
          `/api/challenges/${id}/stages/${stage}/runs`,
          startInit(operationId, bootstrapToken, csrfToken, startOptions.signal),
        );
        const result = await expectParsed(response, [200, 201], parseChallengeStartResult);
        if ((result.kind === 'created') !== (response.status === 201)) {
          throw new ChallengeApiError('INVALID_RESPONSE', response.status);
        }
        if (!callerCredentials) pendingStarts.delete(retryKey);
        return result;
      } catch (error) {
        if (!callerCredentials && error instanceof ChallengeApiError
          && error.code !== 'INVALID_RESPONSE') {
          pendingStarts.delete(retryKey);
        }
        throw error;
      }
    },
    async copyDeck(trackId, stage, csrfToken, copyOptions = {}) {
      const id = parseChallengeTrackId(trackId);
      if (stage !== 1 && stage !== 2 && stage !== 3) throw new Error('stage: expected 1, 2 or 3');
      const retryKey = `${id}:${stage}`;
      const callerOperation = copyOptions.operationId;
      const rememberedOperation = callerOperation === undefined
        ? pendingCopies.get(retryKey)
        : undefined;
      const operationId = parseChallengeOperationId(
        callerOperation ?? rememberedOperation ?? generateOperationId(),
      );
      if (callerOperation === undefined && rememberedOperation === undefined) {
        if (pendingCopies.size >= 32) {
          const oldest = pendingCopies.keys().next().value as string | undefined;
          if (oldest !== undefined) pendingCopies.delete(oldest);
        }
        pendingCopies.set(retryKey, operationId);
      }
      try {
        const response = await browserFetch(
          `/api/challenges/${id}/stages/${stage}/deck-copy`,
          copyDeckInit(operationId, csrfToken, copyOptions.signal),
        );
        const result = await expectParsed(response, [200, 201], parseChallengeDeckCopyResult);
        if ((response.status === 201) !== (result.outcome === 'created')) {
          throw new ChallengeApiError('INVALID_RESPONSE', response.status);
        }
        if (callerOperation === undefined) pendingCopies.delete(retryKey);
        return result;
      } catch (error) {
        if (callerOperation === undefined && error instanceof ChallengeApiError
          && error.code !== 'INVALID_RESPONSE') {
          pendingCopies.delete(retryKey);
        }
        throw error;
      }
    },
  };
}
